import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    FormControl,
    InputLabel,
    InputBase,
    NativeSelect,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
    withStyles,
	Zoom,
	FormHelperText,
	CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import ErrorValidationAlert from '../alerts/ErrorValidationAlert';
import logo from './../../assets/images/logos/m-clean.png';
import ownerService from '../../services/ownerService';
import userService from '../../services/userService';
import SuccessAnimationAlert from '../alerts/SuccessAnimationAlert';

const useStyles = makeStyles((theme) => ({
	label: {
		fontSize: '14px',
		fontWeight: '600',
		textAlign: 'left',
		color: '#000',
		marginBottom: '10px'
	},
	text: {
		fontSize: '14px'
	}
}));

const formSchema = Yup.object().shape({
	review_reason_id: Yup.number().required('Review reason field is required.'),
	review_details: Yup.string().max(1000).nullable(),
});

const BootstrapInput = withStyles((theme) => ({
    root: {
      'label + &': {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid #ced4da',
      fontSize: 16,
      padding: '10px 26px 10px 12px',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      // Use the system font instead of the default Roboto font.
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      '&:focus': {
        borderRadius: 4,
        borderColor: '#80bdff',
        boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
      },
    },
  }))(InputBase);

const ReviewReportDialog = (props) => {

	const classes = useStyles();
	const [errors, setErrors] = useState("");

	const handleOwnerSubmit = (values, formikBag) => {

		setErrors("");
		const { setSubmitting, resetForm } = formikBag;

		const formData = new FormData();
        formData.append('review_id', values.review_id);
        formData.append('review_reason_id', values.review_reason_id);
		formData.append('review_details', values.review_details);

		ownerService.storeReviewReport(formData)
				.then((res) => {
					setSubmitting(false)
					if(res.data.success) {
						resetForm(true)
						props.setConfirmation(true)
					}
				})
				.catch((error) => {
					setErrors(error.response.data.errors)
					setSubmitting(false)
				})
  	}

	const handleUserSubmit = (values, formikBag) => {
		
		setErrors("");
		const { setSubmitting, resetForm } = formikBag;

		const formData = new FormData();
        formData.append('review_id', values.review_id);
        formData.append('review_reason_id', values.review_reason_id);
		formData.append('review_details', values.review_details);

		userService.storeReviewReport(formData)
				.then((res) => {
					setSubmitting(false)
					if(res.data.success) {
						resetForm(true)
						props.setConfirmation(true)
					}
				})
				.catch((error) => {
					setErrors(error.response.data.errors)
					setSubmitting(false)
				})
	}

 	return (
 		<>
	    	<Dialog 
	    		open={props.dialogIsOpen}  
				onClose={(event, reason) => {
                    if(reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        props.handleDialogClose(true)
                    }
                }}
	    		aria-labelledby="form-dialog-review-report"
	    		fullWidth 
	    		maxWidth="xs"
	    		TransitionComponent={Zoom}
	    	>
				<DialogTitle 
					id="form-dialog-review-report" 
					className="dialog-title"
				>
					<img src={logo} className="mr-1" alt="Mulk.pk" />
					Report Review
				</DialogTitle>
				<DialogContent>
					<div className="container">
						{props.confirmation && 
							(
                            	<SuccessAnimationAlert
                                	title="Thank you"
                                	text="Review reported successfully."
                            	/>
                        	)
						}
                        {!props.confirmation && 
							(
								<div className="custom-form-new">  
									<div className="row mt-1 mb-2">
										<div className="col-md-12 col-sm-12 col-xs-12">
											<p className={classes.text}>
												Please refer to our <Link to="#" className="span-link">Content Guidelines</Link> and <Link to="#" className="span-link">Terms of Service</Link> and let us know why you think the content you've reported may violate these guidelines.
											</p>
										</div>
									</div>     
									{errors &&
										<ErrorValidationAlert
											errors={errors} 
										/>
									}               	
									<Formik 
										initialValues={{ 
											review_id: props.reviewId,
											review_reason_id: '',
											review_details: ''
										}}
										validationSchema={formSchema}
										onSubmit={props.isOwner ? handleOwnerSubmit : handleUserSubmit}
									>
									{formik => (
										<form onSubmit={formik.handleSubmit}>
											
												<div className="row">
													<div className="col-md-12 col-sm-12 col-xs-12">
														<InputLabel className={classes.label} htmlFor="review_reason_id">Why do you want to report this review?</InputLabel>
														<FormControl fullWidth>
															<NativeSelect
																id="review_reason_id"
																placeholder="Why do you want to report this review?"
																{...formik.getFieldProps('review_reason_id')}
																input={<BootstrapInput />}
															>
																<option aria-label="None" value="" />
																{props.reasons.map((reason, index) => 
																	<option key={index} value={reason.id}>{reason.question}</option>
																)}
															</NativeSelect>
														</FormControl>
														{formik.errors.review_reason_id &&
															<FormHelperText error={true}>{formik.errors.review_reason_id}</FormHelperText>
														} 
													</div>
												</div>
											
											<div className="row mt-1 mb-1">
												<div className="col-md-12 col-sm-12 col-xs-12">
													<TextField
														id="report_details"
														label="Specific Details"
														multiline
														fullWidth
														{...formik.getFieldProps('review_details')}
														minRows={6}
														placeholder="Please provide specific details"
														variant="outlined"
													/>
													{formik.errors.review_details &&
														<FormHelperText error={true}>{formik.errors.review_details}</FormHelperText>
													}
												</div>
											</div>
											<div className="row">
												<div className="col-md-12 col-sm-12 col-xs-12">
													<Button
														variant="contained"
														color="secondary"
														className="mt-1 mb-2"
														disabled={formik.isSubmitting}
														type="submit"
													>
														{formik.isSubmitting && <CircularProgress size={24} />}
														{!formik.isSubmitting && 'Report'}
													</Button>
												</div>
											</div>
										</form>
										)}
									</Formik>
								</div>
							)
						}
			        </div>
				</DialogContent>
		        <DialogActions className="dialog-footer">
		          	<Button onClick={props.handleDialogClose} color="primary">
		            	Cancel
		          	</Button>
		        </DialogActions>
			</Dialog>
		</>
  	)
}

export default ReviewReportDialog;